import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Package, 
  Users, 
  FileText,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Save,
  Send,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  Building,
  Truck,
  Mail,
  BarChart3,
  PieChart,
  Activity,
  Plus,
  Trash2,
  X,
  UserPlus,
  Scale,
  Landmark,
  Layers
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { API_URL, API_BASE_URL } from "../config/api";


const ShareholderReportsPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('weekly');
  const [loading, setLoading] = useState(false);
  
  // Period selection
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedWeek, setSelectedWeek] = useState(getWeekNumber(new Date()));
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  // Report data
  const [weeklyData, setWeeklyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  
  // Narrative inputs
  const [weeklyNarrative, setWeeklyNarrative] = useState({
    major_developments: ['', '', '', '', ''],
    issues_risks: ['', '', ''],
    focus_next_week: '',
    additional_notes: ''
  });
  
  const [monthlyNarrative, setMonthlyNarrative] = useState({
    executive_summary: '',
    strategic_initiatives_progress: ['', '', ''],
    risks_and_opportunities: '',
    management_focus: '',
    additional_notes: ''
  });
  
  // Saved reports
  const [savedReports, setSavedReports] = useState([]);
  
  // Email sending state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailReportType, setEmailReportType] = useState('weekly');
  const [shareholders, setShareholders] = useState([]);
  const [selectedShareholders, setSelectedShareholders] = useState([]);
  const [customMessage, setCustomMessage] = useState('');
  const [includeNarrative, setIncludeNarrative] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Add shareholder state
  const [showAddShareholder, setShowAddShareholder] = useState(false);
  const [newShareholder, setNewShareholder] = useState({ name: '', email: '', role: 'Shareholder' });
  
  // Send logs
  const [sendLogs, setSendLogs] = useState([]);

  // Get week number
  function getWeekNumber(d) {
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  }

  // Fetch weekly data
  const fetchWeeklyData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/shareholder-reports/weekly-data?year=${selectedYear}&week=${selectedWeek}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWeeklyData(res.data);
    } catch (error) {
      toast.error('Failed to fetch weekly data');
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear, selectedWeek]);

  // Fetch monthly data
  const fetchMonthlyData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/shareholder-reports/monthly-data?year=${selectedYear}&month=${selectedMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMonthlyData(res.data);
    } catch (error) {
      toast.error('Failed to fetch monthly data');
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear, selectedMonth]);

  // Fetch saved reports
  const fetchSavedReports = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/shareholder-reports/saved-reports?year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavedReports(res.data.reports);
    } catch (error) {
      console.error('Failed to fetch saved reports');
    }
  }, [token, selectedYear]);

  useEffect(() => {
    if (activeTab === 'weekly') {
      fetchWeeklyData();
    } else if (activeTab === 'monthly') {
      fetchMonthlyData();
    }
    fetchSavedReports();
  }, [activeTab, fetchWeeklyData, fetchMonthlyData, fetchSavedReports]);

  // Save weekly report
  const saveWeeklyReport = async () => {
    try {
      await axios.post(
        `${API_URL}/shareholder-reports/save-weekly?year=${selectedYear}&week=${selectedWeek}`,
        {
          major_developments: weeklyNarrative.major_developments.filter(d => d.trim()),
          issues_risks: weeklyNarrative.issues_risks.filter(r => r.trim()),
          focus_next_week: weeklyNarrative.focus_next_week,
          additional_notes: weeklyNarrative.additional_notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Weekly report saved');
      fetchSavedReports();
    } catch (error) {
      toast.error('Failed to save report');
    }
  };

  // Save monthly report
  const saveMonthlyReport = async () => {
    try {
      await axios.post(
        `${API_URL}/shareholder-reports/save-monthly?year=${selectedYear}&month=${selectedMonth}`,
        {
          executive_summary: monthlyNarrative.executive_summary,
          strategic_initiatives_progress: monthlyNarrative.strategic_initiatives_progress.filter(s => s.trim()),
          risks_and_opportunities: monthlyNarrative.risks_and_opportunities,
          management_focus: monthlyNarrative.management_focus,
          additional_notes: monthlyNarrative.additional_notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Monthly report saved');
      fetchSavedReports();
    } catch (error) {
      toast.error('Failed to save report');
    }
  };

  // Export PDF functions
  const exportWeeklyPDF = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/shareholder-reports/export-weekly-pdf?year=${selectedYear}&week=${selectedWeek}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Europacific_Weekly_Report_W${selectedWeek}_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const exportMonthlyPDF = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/shareholder-reports/export-monthly-pdf?year=${selectedYear}&month=${selectedMonth}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      link.setAttribute('download', `Europacific_Monthly_Report_${months[selectedMonth-1]}_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  // Fetch shareholders
  const fetchShareholders = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/shareholder-reports/shareholders`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShareholders(res.data.shareholders || []);
    } catch (error) {
      console.error('Failed to fetch shareholders');
    }
  }, [token]);

  // Fetch send logs
  const fetchSendLogs = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/shareholder-reports/send-logs?limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSendLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch send logs');
    }
  }, [token]);

  useEffect(() => {
    fetchShareholders();
    fetchSendLogs();
  }, [fetchShareholders, fetchSendLogs]);

  // Open email dialog
  const openEmailDialog = (reportType) => {
    setEmailReportType(reportType);
    setSelectedShareholders(shareholders.map(s => s.id));
    setCustomMessage('');
    setIncludeNarrative(true);
    setShowEmailDialog(true);
  };

  // Send report email
  const sendReportEmail = async () => {
    if (selectedShareholders.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    setSendingEmail(true);
    try {
      const recipients = shareholders
        .filter(s => selectedShareholders.includes(s.id))
        .map(s => ({ name: s.name, email: s.email }));

      const endpoint = emailReportType === 'weekly' 
        ? `${API_URL}/shareholder-reports/send-weekly?year=${selectedYear}&week=${selectedWeek}`
        : `${API_URL}/shareholder-reports/send-monthly?year=${selectedYear}&month=${selectedMonth}`;

      const res = await axios.post(
        endpoint,
        {
          recipients,
          include_narrative: includeNarrative,
          custom_message: customMessage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        toast.success(`Report sent to ${res.data.sent_to.length} recipient(s)`);
        if (res.data.failed?.length > 0) {
          toast.warning(`Failed to send to ${res.data.failed.length} recipient(s)`);
        }
        setShowEmailDialog(false);
        fetchSendLogs();
      } else {
        toast.error('Failed to send report');
      }
    } catch (error) {
      toast.error('Failed to send report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingEmail(false);
    }
  };

  // Add shareholder
  const addShareholder = async () => {
    if (!newShareholder.name || !newShareholder.email) {
      toast.error('Name and email are required');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/shareholder-reports/shareholders`,
        newShareholder,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Shareholder added');
      setNewShareholder({ name: '', email: '', role: 'Shareholder' });
      setShowAddShareholder(false);
      fetchShareholders();
    } catch (error) {
      toast.error('Failed to add shareholder: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Remove shareholder
  const removeShareholder = async (shareholderId) => {
    try {
      await axios.delete(
        `${API_URL}/shareholder-reports/shareholders/${shareholderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Shareholder removed');
      fetchShareholders();
    } catch (error) {
      toast.error('Failed to remove shareholder');
    }
  };

  // Metric card component
  const MetricCard = ({ title, value, change, icon: Icon, trend, subtitle }) => (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-sm">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className="flex flex-col items-end">
            <div className={`p-2 rounded-lg ${trend === 'up' ? 'bg-green-500/20' : trend === 'down' ? 'bg-red-500/20' : 'bg-slate-700'}`}>
              <Icon className={`h-5 w-5 ${trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-slate-400'}`} />
            </div>
            {change !== undefined && (
              <div className={`flex items-center mt-2 text-sm ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {change >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                {Math.abs(change)}%
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Week selector
  const WeekSelector = () => (
    <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-2">
      <Button variant="ghost" size="sm" onClick={() => setSelectedWeek(w => Math.max(1, w - 1))}>
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <div className="text-center min-w-[120px]">
        <span className="text-white font-medium">Week {selectedWeek}</span>
        <span className="text-slate-400 text-sm ml-2">{selectedYear}</span>
      </div>
      <Button variant="ghost" size="sm" onClick={() => setSelectedWeek(w => Math.min(53, w + 1))}>
        <ChevronRight className="h-4 w-4" />
      </Button>
      <Input 
        type="number" 
        value={selectedYear} 
        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
        className="w-20 bg-slate-700 border-slate-600 text-white"
      />
    </div>
  );

  // Month selector
  const MonthSelector = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return (
      <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-2">
        <Button variant="ghost" size="sm" onClick={() => setSelectedMonth(m => Math.max(1, m - 1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-center min-w-[120px]">
          <span className="text-white font-medium">{months[selectedMonth - 1]}</span>
          <span className="text-slate-400 text-sm ml-2">{selectedYear}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setSelectedMonth(m => Math.min(12, m + 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Input 
          type="number" 
          value={selectedYear} 
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          className="w-20 bg-slate-700 border-slate-600 text-white"
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Navbar />
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-blue-400" />
              Shareholder Reports
            </h1>
            <p className="text-slate-400 mt-1">Automated weekly pulse and monthly performance reports</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => activeTab === 'weekly' ? fetchWeeklyData() : fetchMonthlyData()}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh Data
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="weekly" className="data-[state=active]:bg-blue-600">
              <Clock className="h-4 w-4 mr-2" />
              Weekly Pulse
            </TabsTrigger>
            <TabsTrigger value="monthly" className="data-[state=active]:bg-blue-600">
              <Calendar className="h-4 w-4 mr-2" />
              Monthly Report
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-blue-600">
              <FileText className="h-4 w-4 mr-2" />
              Report History
            </TabsTrigger>
          </TabsList>

          {/* Weekly Report Tab */}
          <TabsContent value="weekly" className="space-y-6">
            <div className="flex items-center justify-between">
              <WeekSelector />
              <div className="flex gap-2">
                <Button variant="outline" onClick={exportWeeklyPDF} data-testid="export-weekly-pdf">
                  <Download className="h-4 w-4 mr-2" />
                  Export PDF
                </Button>
                <Button variant="outline" onClick={() => openEmailDialog('weekly')} className="text-green-400 border-green-600 hover:bg-green-600/20" data-testid="send-weekly-email">
                  <Send className="h-4 w-4 mr-2" />
                  Send to Shareholders
                </Button>
                <Button onClick={saveWeeklyReport} className="bg-blue-600 hover:bg-blue-700" data-testid="save-weekly-report">
                  <Save className="h-4 w-4 mr-2" />
                  Save Report
                </Button>
              </div>
            </div>

            {weeklyData && (
              <>
                {/* Key Metrics Dashboard */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <MetricCard 
                    title="New Orders" 
                    value={weeklyData.key_metrics.new_orders}
                    change={weeklyData.key_metrics.orders_vs_prev_week}
                    icon={Package}
                    trend={weeklyData.key_metrics.orders_vs_prev_week > 0 ? 'up' : weeklyData.key_metrics.orders_vs_prev_week < 0 ? 'down' : 'neutral'}
                  />
                  <MetricCard 
                    title="Active Shipments" 
                    value={weeklyData.key_metrics.active_shipments}
                    icon={Truck}
                  />
                  <MetricCard 
                    title="Completed" 
                    value={weeklyData.key_metrics.completed_shipments}
                    icon={CheckCircle}
                    trend="up"
                  />
                  <MetricCard 
                    title="Cash Balance" 
                    value={`€${(weeklyData.key_metrics.cash_balance || 0).toLocaleString()}`}
                    icon={DollarSign}
                  />
                  <MetricCard 
                    title="New Quotes" 
                    value={weeklyData.sales_pipeline.new_quote_requests}
                    icon={FileText}
                  />
                  <MetricCard 
                    title="Partner Network" 
                    value={weeklyData.operational.partner_network_size}
                    icon={Users}
                  />
                </div>

                {/* Detailed Sections */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Sales Pipeline */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <PieChart className="h-5 w-5 text-purple-400" />
                        Sales Pipeline
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between text-slate-300">
                        <span>New Quote Requests</span>
                        <span className="font-semibold">{weeklyData.sales_pipeline.new_quote_requests}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>Pending Quotes</span>
                        <span className="font-semibold">{weeklyData.sales_pipeline.pending_quotes}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>Quotes Converted</span>
                        <span className="font-semibold">{weeklyData.sales_pipeline.quotes_converted}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>New Offers</span>
                        <span className="font-semibold">{weeklyData.sales_pipeline.new_offers}</span>
                      </div>
                      <div className="flex justify-between text-slate-300 pt-2 border-t border-slate-700">
                        <span>Conversion Rate</span>
                        <Badge variant={weeklyData.sales_pipeline.conversion_rate > 30 ? "default" : "secondary"}>
                          {weeklyData.sales_pipeline.conversion_rate}%
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Operational KPIs */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Activity className="h-5 w-5 text-green-400" />
                        Operational KPIs
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between text-slate-300">
                        <span>New Shipments</span>
                        <span className="font-semibold">{weeklyData.shipments.new_shipments}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>BL Documents Created</span>
                        <span className="font-semibold">{weeklyData.operational.new_bl_documents}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>Emails Processed</span>
                        <span className="font-semibold">{weeklyData.operational.emails_processed}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>Active Container Tracking</span>
                        <span className="font-semibold">{weeklyData.operational.active_container_tracking}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Verticals & Izvrsbe Section */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Business Verticals Breakdown */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Layers className="h-5 w-5 text-indigo-400" />
                        Business Verticals
                      </CardTitle>
                      <CardDescription>Orders and revenue per company vertical</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {weeklyData.verticals && Object.entries(weeklyData.verticals).map(([vertical, data]) => (
                        <div key={vertical} className="flex justify-between items-center text-slate-300 py-2 border-b border-slate-700/50 last:border-0">
                          <span className="capitalize font-medium">{vertical.replace('_', ' ')}</span>
                          <div className="flex gap-4 text-right">
                            <div>
                              <span className="text-xs text-slate-400 block">Orders</span>
                              <span className="font-semibold">{data.orders}</span>
                            </div>
                            <div>
                              <span className="text-xs text-slate-400 block">Revenue</span>
                              <span className="font-semibold text-green-400">€{data.revenue?.toLocaleString() || 0}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Izvršbe (Court Enforcements) */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Scale className="h-5 w-5 text-red-400" />
                        Izvršbe (Enforcements)
                      </CardTitle>
                      <CardDescription>Active court executions by vertical</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {weeklyData.izvrsbe?.by_vertical && Object.entries(weeklyData.izvrsbe.by_vertical).map(([vertical, data]) => (
                        <div key={vertical} className="flex justify-between items-center text-slate-300 py-2 border-b border-slate-700/50 last:border-0">
                          <span className="capitalize font-medium">{vertical.replace('_', ' ')}</span>
                          <div className="flex gap-4 text-right">
                            <div>
                              <span className="text-xs text-slate-400 block">Cases</span>
                              <span className="font-semibold">{data.count}</span>
                            </div>
                            <div>
                              <span className="text-xs text-slate-400 block">Outstanding</span>
                              <span className="font-semibold text-red-400">€{data.outstanding?.toLocaleString() || 0}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {/* Summary row */}
                      <div className="pt-2 border-t border-slate-600">
                        <div className="flex justify-between text-slate-300">
                          <span>Total Active Cases</span>
                          <Badge variant="destructive">{weeklyData.izvrsbe?.total_active_cases || 0}</Badge>
                        </div>
                        <div className="flex justify-between text-slate-300 mt-2">
                          <span>Total Outstanding</span>
                          <span className="font-bold text-red-400">€{weeklyData.izvrsbe?.total_outstanding?.toLocaleString() || 0}</span>
                        </div>
                        <div className="flex justify-between text-slate-300 mt-2">
                          <span className="flex items-center gap-1">
                            <AlertTriangle className="h-4 w-4 text-yellow-400" />
                            Upcoming Deadlines (7 days)
                          </span>
                          <Badge variant={weeklyData.izvrsbe?.upcoming_deadlines_7d > 0 ? "destructive" : "secondary"}>
                            {weeklyData.izvrsbe?.upcoming_deadlines_7d || 0}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Loans Summary */}
                {weeklyData.loans && Object.keys(weeklyData.loans.by_company || {}).length > 0 && (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Landmark className="h-5 w-5 text-amber-400" />
                        Loans Outstanding
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-4 gap-4">
                        {Object.entries(weeklyData.loans.by_company).map(([company, data]) => (
                          <div key={company} className="bg-slate-700/30 p-3 rounded-lg">
                            <span className="text-slate-400 text-sm block capitalize">{company}</span>
                            <span className="text-white font-semibold text-lg">€{data.outstanding?.toLocaleString() || 0}</span>
                            <span className="text-slate-400 text-xs block">{data.count} loan(s)</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t border-slate-600 flex justify-between">
                        <span className="text-slate-300 font-medium">Total Debt</span>
                        <span className="text-amber-400 font-bold text-lg">€{weeklyData.loans.total_debt?.toLocaleString() || 0}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Narrative Sections - What you need to write */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Major Developments (3-5 bullet points)</CardTitle>
                    <CardDescription>Key events: new contracts, large customers, partnerships, key hires, disruptions</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {weeklyNarrative.major_developments.map((dev, idx) => (
                      <Input
                        key={idx}
                        value={dev}
                        onChange={(e) => {
                          const newDevs = [...weeklyNarrative.major_developments];
                          newDevs[idx] = e.target.value;
                          setWeeklyNarrative({...weeklyNarrative, major_developments: newDevs});
                        }}
                        placeholder={`Development ${idx + 1}...`}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    ))}
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-yellow-400" />
                      Issues / Risks
                    </CardTitle>
                    <CardDescription>Payment delays, capacity constraints, regulatory issues, supply chain problems</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {weeklyNarrative.issues_risks.map((risk, idx) => (
                      <Input
                        key={idx}
                        value={risk}
                        onChange={(e) => {
                          const newRisks = [...weeklyNarrative.issues_risks];
                          newRisks[idx] = e.target.value;
                          setWeeklyNarrative({...weeklyNarrative, issues_risks: newRisks});
                        }}
                        placeholder={`Risk/Issue ${idx + 1}...`}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    ))}
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Focus for Next Week</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={weeklyNarrative.focus_next_week}
                      onChange={(e) => setWeeklyNarrative({...weeklyNarrative, focus_next_week: e.target.value})}
                      placeholder="What management is prioritizing next week..."
                      className="bg-slate-700 border-slate-600 text-white min-h-[100px]"
                    />
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Monthly Report Tab */}
          <TabsContent value="monthly" className="space-y-6">
            <div className="flex items-center justify-between">
              <MonthSelector />
              <div className="flex gap-2">
                <Button variant="outline" onClick={exportMonthlyPDF} data-testid="export-monthly-pdf">
                  <Download className="h-4 w-4 mr-2" />
                  Export PDF
                </Button>
                <Button variant="outline" onClick={() => openEmailDialog('monthly')} className="text-green-400 border-green-600 hover:bg-green-600/20" data-testid="send-monthly-email">
                  <Send className="h-4 w-4 mr-2" />
                  Send to Shareholders
                </Button>
                <Button onClick={saveMonthlyReport} className="bg-blue-600 hover:bg-blue-700" data-testid="save-monthly-report">
                  <Save className="h-4 w-4 mr-2" />
                  Save Report
                </Button>
              </div>
            </div>

            {monthlyData && (
              <>
                {/* Executive Summary Dashboard */}
                <Card className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 border-blue-700">
                  <CardHeader>
                    <CardTitle className="text-white text-xl">Executive Summary - {monthlyData.period.month_name} {monthlyData.period.year}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      <div>
                        <p className="text-slate-400 text-sm">Total Orders</p>
                        <p className="text-3xl font-bold text-white">{monthlyData.executive_summary_data.total_orders}</p>
                        <p className={`text-sm ${monthlyData.executive_summary_data.vs_previous_period >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {monthlyData.executive_summary_data.vs_previous_period >= 0 ? '+' : ''}{monthlyData.executive_summary_data.vs_previous_period}% vs prev
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-sm">Revenue</p>
                        <p className="text-3xl font-bold text-white">€{monthlyData.executive_summary_data.total_revenue.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-sm">New Customers</p>
                        <p className="text-3xl font-bold text-white">{monthlyData.executive_summary_data.new_customers}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-sm">Cash Position</p>
                        <p className="text-3xl font-bold text-white">€{monthlyData.executive_summary_data.cash_position.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Financial Performance */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-green-400" />
                      Financial Performance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center p-4 bg-slate-700/50 rounded-lg">
                        <p className="text-slate-400 text-sm">Revenue</p>
                        <p className="text-xl font-bold text-white">€{monthlyData.financial_performance.revenue.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-slate-700/50 rounded-lg">
                        <p className="text-slate-400 text-sm">Avg Order Value</p>
                        <p className="text-xl font-bold text-white">€{monthlyData.financial_performance.avg_order_value.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-slate-700/50 rounded-lg">
                        <p className="text-slate-400 text-sm">Receivables</p>
                        <p className="text-xl font-bold text-white">€{monthlyData.financial_performance.receivables.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-slate-700/50 rounded-lg">
                        <p className="text-slate-400 text-sm">Cash Balance</p>
                        <p className="text-xl font-bold text-green-400">€{monthlyData.financial_performance.cash_balance.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-slate-700/50 rounded-lg">
                        <p className="text-slate-400 text-sm">Debt</p>
                        <p className="text-xl font-bold text-red-400">€{monthlyData.financial_performance.debt.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Weekly Trend */}
                {monthlyData.weekly_trend && monthlyData.weekly_trend.length > 0 && (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white">Weekly Trend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-4 overflow-x-auto pb-2">
                        {monthlyData.weekly_trend.map((week, idx) => (
                          <div key={idx} className="flex-shrink-0 text-center p-4 bg-slate-700/50 rounded-lg min-w-[100px]">
                            <p className="text-slate-400 text-xs">Week {week.week}</p>
                            <p className="text-slate-500 text-xs">{week.start} - {week.end}</p>
                            <p className="text-2xl font-bold text-white mt-2">{week.orders}</p>
                            <p className="text-slate-400 text-xs">orders</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Detailed Metrics Grid */}
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Operational KPIs */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white text-lg">Operational KPIs</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between text-slate-300">
                        <span>Total Shipments</span>
                        <span className="font-semibold">{monthlyData.operational_kpis.total_shipments}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>Completed</span>
                        <span className="font-semibold">{monthlyData.operational_kpis.completed_shipments}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>Active</span>
                        <span className="font-semibold">{monthlyData.operational_kpis.active_shipments}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>BLs Created</span>
                        <span className="font-semibold">{monthlyData.operational_kpis.bl_documents_created}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>Emails Processed</span>
                        <span className="font-semibold">{monthlyData.operational_kpis.emails_processed}</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Sales Pipeline */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white text-lg">Sales Pipeline</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between text-slate-300">
                        <span>New Quotes</span>
                        <span className="font-semibold">{monthlyData.sales_pipeline.new_quote_requests}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>Pending</span>
                        <span className="font-semibold">{monthlyData.sales_pipeline.pending_quotes}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>Converted</span>
                        <span className="font-semibold">{monthlyData.sales_pipeline.quotes_converted}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>New Offers</span>
                        <span className="font-semibold">{monthlyData.sales_pipeline.new_offers}</span>
                      </div>
                      <div className="flex justify-between text-slate-300 pt-2 border-t border-slate-700">
                        <span>Conversion Rate</span>
                        <Badge>{monthlyData.sales_pipeline.conversion_rate}%</Badge>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Customer Metrics */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white text-lg">Customers & Partners</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between text-slate-300">
                        <span>New Companies</span>
                        <span className="font-semibold">{monthlyData.customer_metrics.new_companies}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>Total Companies</span>
                        <span className="font-semibold">{monthlyData.customer_metrics.total_companies}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>New Leads</span>
                        <span className="font-semibold">{monthlyData.customer_metrics.new_leads}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>Active Leads</span>
                        <span className="font-semibold">{monthlyData.customer_metrics.active_leads}</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>Partner Network</span>
                        <span className="font-semibold">{monthlyData.partner_network.total_partners}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Verticals & Izvrsbe for Monthly Report */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Business Verticals Breakdown */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Layers className="h-5 w-5 text-indigo-400" />
                        Business Verticals
                      </CardTitle>
                      <CardDescription>Performance per company vertical</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {monthlyData.verticals && Object.entries(monthlyData.verticals).map(([vertical, data]) => (
                        <div key={vertical} className="flex justify-between items-center text-slate-300 py-2 border-b border-slate-700/50 last:border-0">
                          <span className="capitalize font-medium">{vertical.replace('_', ' ')}</span>
                          <div className="flex gap-4 text-right">
                            <div>
                              <span className="text-xs text-slate-400 block">Orders</span>
                              <span className="font-semibold">{data.orders}</span>
                            </div>
                            <div>
                              <span className="text-xs text-slate-400 block">Revenue</span>
                              <span className="font-semibold text-green-400">€{data.revenue?.toLocaleString() || 0}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Izvršbe (Court Enforcements) */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Scale className="h-5 w-5 text-red-400" />
                        Izvršbe (Enforcements)
                      </CardTitle>
                      <CardDescription>Court execution cases by vertical</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {monthlyData.izvrsbe?.by_vertical && Object.entries(monthlyData.izvrsbe.by_vertical).map(([vertical, data]) => (
                        <div key={vertical} className="flex justify-between items-center text-slate-300 py-2 border-b border-slate-700/50 last:border-0">
                          <span className="capitalize font-medium">{vertical.replace('_', ' ')}</span>
                          <div className="flex gap-4 text-right">
                            <div>
                              <span className="text-xs text-slate-400 block">Cases</span>
                              <span className="font-semibold">{data.count}</span>
                            </div>
                            <div>
                              <span className="text-xs text-slate-400 block">Outstanding</span>
                              <span className="font-semibold text-red-400">€{data.outstanding?.toLocaleString() || 0}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="pt-2 border-t border-slate-600">
                        <div className="flex justify-between text-slate-300">
                          <span>Total Active Cases</span>
                          <Badge variant="destructive">{monthlyData.izvrsbe?.total_active_cases || 0}</Badge>
                        </div>
                        <div className="flex justify-between text-slate-300 mt-2">
                          <span>Total Outstanding</span>
                          <span className="font-bold text-red-400">€{monthlyData.izvrsbe?.total_outstanding?.toLocaleString() || 0}</span>
                        </div>
                        <div className="flex justify-between text-slate-300 mt-2">
                          <span>Payments This Period</span>
                          <Badge variant="secondary">{monthlyData.izvrsbe?.recent_payments || 0}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Loans Summary for Monthly */}
                {monthlyData.loans && Object.keys(monthlyData.loans.by_company || {}).length > 0 && (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Landmark className="h-5 w-5 text-amber-400" />
                        Loans Outstanding
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-4 gap-4">
                        {Object.entries(monthlyData.loans.by_company).map(([company, data]) => (
                          <div key={company} className="bg-slate-700/30 p-3 rounded-lg">
                            <span className="text-slate-400 text-sm block capitalize">{company}</span>
                            <span className="text-white font-semibold text-lg">€{data.outstanding?.toLocaleString() || 0}</span>
                            <span className="text-slate-400 text-xs block">{data.count} loan(s)</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t border-slate-600 flex justify-between">
                        <span className="text-slate-300 font-medium">Total Debt</span>
                        <span className="text-amber-400 font-bold text-lg">€{monthlyData.loans.total_debt?.toLocaleString() || 0}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Narrative Sections */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Executive Summary</CardTitle>
                    <CardDescription>What happened this month, above/below plan, management focus</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={monthlyNarrative.executive_summary}
                      onChange={(e) => setMonthlyNarrative({...monthlyNarrative, executive_summary: e.target.value})}
                      placeholder="Overall month summary..."
                      className="bg-slate-700 border-slate-600 text-white min-h-[120px]"
                    />
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Strategic Initiatives Progress</CardTitle>
                    <CardDescription>Market expansion, M&A, technology investments, cost reduction</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {monthlyNarrative.strategic_initiatives_progress.map((init, idx) => (
                      <Input
                        key={idx}
                        value={init}
                        onChange={(e) => {
                          const newInits = [...monthlyNarrative.strategic_initiatives_progress];
                          newInits[idx] = e.target.value;
                          setMonthlyNarrative({...monthlyNarrative, strategic_initiatives_progress: newInits});
                        }}
                        placeholder={`Initiative ${idx + 1}...`}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    ))}
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-yellow-400" />
                      Risks and Opportunities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={monthlyNarrative.risks_and_opportunities}
                      onChange={(e) => setMonthlyNarrative({...monthlyNarrative, risks_and_opportunities: e.target.value})}
                      placeholder="Key risks emerging and opportunities being pursued..."
                      className="bg-slate-700 border-slate-600 text-white min-h-[100px]"
                    />
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Report History Tab */}
          <TabsContent value="history" className="space-y-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Saved Reports</CardTitle>
              </CardHeader>
              <CardContent>
                {savedReports.length === 0 ? (
                  <p className="text-slate-400 text-center py-8">No reports saved yet</p>
                ) : (
                  <div className="space-y-3">
                    {savedReports.map((report) => (
                      <div key={report.id} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant={report.report_type === 'weekly' ? 'default' : 'secondary'}>
                              {report.report_type}
                            </Badge>
                            <span className="text-white font-medium">
                              {report.report_type === 'weekly' 
                                ? `Week ${report.week}, ${report.year}`
                                : `${report.month_name} ${report.year}`
                              }
                            </span>
                            <Badge variant={report.status === 'finalized' ? 'default' : 'outline'} className={report.status === 'finalized' ? 'bg-green-600' : ''}>
                              {report.status}
                            </Badge>
                          </div>
                          <p className="text-slate-400 text-sm mt-1">
                            Created: {new Date(report.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm">
                            <Download className="h-4 w-4 mr-1" />
                            Export
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-400" />
              Send {emailReportType === 'weekly' ? 'Weekly' : 'Monthly'} Report
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {emailReportType === 'weekly' 
                ? `Week ${selectedWeek}, ${selectedYear}` 
                : `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][selectedMonth-1]} ${selectedYear}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Recipients Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-white">Recipients</Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowAddShareholder(true)}
                  className="text-blue-400 hover:text-blue-300"
                >
                  <UserPlus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              
              {shareholders.length === 0 ? (
                <p className="text-slate-400 text-sm py-4 text-center">
                  No shareholders configured. Add some to send reports.
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {shareholders.map((sh) => (
                    <div key={sh.id} className="flex items-center justify-between p-2 bg-slate-700/50 rounded">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedShareholders.includes(sh.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedShareholders([...selectedShareholders, sh.id]);
                            } else {
                              setSelectedShareholders(selectedShareholders.filter(id => id !== sh.id));
                            }
                          }}
                        />
                        <div>
                          <p className="text-white text-sm">{sh.name}</p>
                          <p className="text-slate-400 text-xs">{sh.email}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeShareholder(sh.id)}
                        className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Options */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="include-narrative"
                checked={includeNarrative}
                onCheckedChange={setIncludeNarrative}
              />
              <Label htmlFor="include-narrative" className="text-slate-300 text-sm cursor-pointer">
                Include management commentary (narrative sections)
              </Label>
            </div>

            {/* Custom Message */}
            <div>
              <Label className="text-white mb-2 block">Custom Message (optional)</Label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Add a personal note to the email..."
                className="bg-slate-700 border-slate-600 text-white min-h-[80px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendReportEmail} 
              disabled={sendingEmail || selectedShareholders.length === 0}
              className="bg-green-600 hover:bg-green-700"
            >
              {sendingEmail ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Shareholder Dialog */}
      <Dialog open={showAddShareholder} onOpenChange={setShowAddShareholder}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Add Shareholder</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a new recipient for shareholder reports
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-white mb-2 block">Name *</Label>
              <Input
                value={newShareholder.name}
                onChange={(e) => setNewShareholder({...newShareholder, name: e.target.value})}
                placeholder="John Smith"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Email *</Label>
              <Input
                type="email"
                value={newShareholder.email}
                onChange={(e) => setNewShareholder({...newShareholder, email: e.target.value})}
                placeholder="john@example.com"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Role</Label>
              <select
                value={newShareholder.role}
                onChange={(e) => setNewShareholder({...newShareholder, role: e.target.value})}
                className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2"
              >
                <option value="Shareholder">Shareholder</option>
                <option value="Board Member">Board Member</option>
                <option value="Investor">Investor</option>
                <option value="Advisor">Advisor</option>
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddShareholder(false)}>
              Cancel
            </Button>
            <Button onClick={addShareholder} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Shareholder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default ShareholderReportsPage;
